import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Loader2, CheckCircle, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { useRoomFavorites } from '../configurator/useRoomFavorites';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CameraBadge = ({ camera }) => camera === 'straight-on' ? null : (
  <span className={`text-[9px] uppercase font-bold px-1 rounded ${camera === 'corner' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>{camera}</span>
);

/* Batch AI scene regeneration: collection × chosen rooms, replace or fill missing, with cost estimate */
export const RegenCollectionDialog = ({ allColors, token }) => {
  const [open, setOpen] = useState(false);
  const [collection, setCollection] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [selected, setSelected] = useState([]);
  const [mode, setMode] = useState('missing');
  const [estimate, setEstimate] = useState(null);
  const [job, setJob] = useState(null);
  const { favorites, loaded, toggle } = useRoomFavorites(token);
  const pollRef = useRef(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => { axios.get(`${API_URL}/admin/fabrics/room-types`).then(({ data }) => setRoomTypes(data)).catch(() => {}); }, []);
  useEffect(() => { if (loaded && open) setSelected(favorites.length ? favorites : ['penthouse_living']); }, [loaded, open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearInterval(pollRef.current), []);

  const sortedRooms = useMemo(() => [...roomTypes].sort((a, b) => favorites.includes(b.id) - favorites.includes(a.id)), [roomTypes, favorites]);
  const body = useMemo(() => ({ collection_name: collection === '__ALL__' ? null : collection, room_types: selected, mode }), [collection, selected, mode]);

  useEffect(() => {
    setEstimate(null);
    if (!collection || !selected.length) return undefined;
    const t = setTimeout(() => {
      axios.post(`${API_URL}/admin/fabrics/colors/regenerate-all/estimate`, body, { headers })
        .then(({ data }) => setEstimate(data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [body, collection, selected, headers]);

  const toggleRoom = (id) => setSelected(s => (s.includes(id) ? s.filter(r => r !== id) : [...s, id]));

  const start = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/admin/fabrics/colors/regenerate-all`, body, { headers });
      toast.success(`Started regeneration: ${data.total} scenes`);
      setJob({ id: data.job_id, status: 'running', total: data.total, completed: 0 });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await axios.get(`${API_URL}/admin/fabrics/visualization-jobs/${data.job_id}`, { headers });
          const failed = (j.results || []).filter(r => r.status === 'error').length;
          setJob(prev => ({ ...prev, status: j.status, completed: j.completed || 0, failed }));
          if (j.status !== 'running') { clearInterval(pollRef.current); toast.success(`Regeneration complete (${failed} failed)`); }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start regeneration');
    }
  };

  const collections = [...new Set(allColors.map(c => c.collection_name).filter(Boolean))].sort();
  const tooMany = estimate && estimate.scene_count > estimate.max_scenes;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" data-testid="regen-collection-btn">
          <RefreshCw className="w-4 h-4" /> Regenerate Collection
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Regenerate AI Room Scenes</DialogTitle></DialogHeader>
        <div className="py-2 space-y-4">
          <div>
            <Label>Collection</Label>
            <select className="w-full mt-1 rounded border border-neutral-300 p-2 text-sm" value={collection} onChange={e => setCollection(e.target.value)} data-testid="regen-collection-select">
              <option value="">Select collection...</option>
              {collections.map(name => <option key={name} value={name}>{name} ({allColors.filter(c => c.collection_name === name).length} colors)</option>)}
              <option value="__ALL__">ALL Collections ({allColors.length} colors)</option>
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Room types</Label>
              <span className="text-[11px] text-neutral-400">Star = favourite (pre-selected next time)</span>
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto" data-testid="regen-room-list">
              {sortedRooms.map(r => (
                <div key={r.id} className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs ${selected.includes(r.id) ? 'border-amber-500 bg-amber-50' : 'border-neutral-200'}`}>
                  <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleRoom(r.id)} data-testid={`regen-room-${r.id}`} />
                  <span className="truncate flex-1">{r.label}</span>
                  <CameraBadge camera={r.camera} />
                  <button type="button" onClick={() => toggle(r.id)} title="Favourite" data-testid={`regen-fav-${r.id}`}>
                    <Star className={`w-3.5 h-3.5 ${favorites.includes(r.id) ? 'fill-amber-400 text-amber-500' : 'text-neutral-300'}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2" data-testid="regen-mode">
            {[['missing', 'Only missing scenes'], ['replace', 'Replace existing']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setMode(k)} className={`flex-1 rounded-md border px-3 py-2 text-xs font-medium ${mode === k ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600'}`} data-testid={`regen-mode-${k}`}>{label}</button>
            ))}
          </div>
          {estimate && (
            <div className={`rounded-lg border p-3 text-sm ${tooMany ? 'border-red-200 bg-red-50 text-red-700' : 'border-neutral-200 bg-neutral-50 text-neutral-700'}`} data-testid="regen-estimate">
              <b>{estimate.scene_count}</b> scenes · {estimate.color_count} colors · ≈ <b>${estimate.estimated_cost_usd}</b> · ~{estimate.estimated_minutes} min
              {tooMany && <p className="text-xs mt-1">Over the {estimate.max_scenes}-scene limit — pick a collection or fewer rooms.</p>}
            </div>
          )}
          {job && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="regen-job-progress">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
                {job.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {job.status === 'running' ? 'Regenerating...' : 'Complete'}
              </div>
              <p className="text-xs text-blue-600 mt-1">{job.completed || 0} / {job.total} completed{job.failed > 0 && ` (${job.failed} failed)`}</p>
              <div className="w-full bg-blue-200 rounded-full h-1.5 mt-2">
                <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${job.total ? (job.completed / job.total * 100) : 0}%` }} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button onClick={start} disabled={!estimate?.scene_count || tooMany || job?.status === 'running'} data-testid="regen-collection-start-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Start Regeneration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
